import { filterByQuery, sortVariables } from '@/util/sort';
import IconButton from '@/components/common/IconButton';
import SearchField from '@/components/common/SearchField';
import FindReferencesDialog from '@/components/navigation/FindReferences';
import VariableDialog from '@/components/variables/VariableDialog';
import { useApplication } from '@/hooks/useApplication';
import { useMetadata } from '@/hooks/useMetadata';
import { SearchBlocksItem } from '@/types/search';
import { Variable, VariableRef, VariableScope, VariableSortType } from '@/types/variable';
import { findSubroutine } from '@/util/application';
import { variableCustomTooltip, variableIsInUse, variableSearchOptions } from '@/util/variables';
import { Tune } from '@mui/icons-material';
import ChevronRight from '@mui/icons-material/ChevronRight';
import CircleIcon from '@mui/icons-material/Circle';
import ExpandMore from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Stack,
  Tooltip,
  Typography
} from '@mui/material';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

const defaultCollapse = { config: false, global: false, task: false, session: false, channel: false, local: false };

type VariablePickerProps = {
  open: boolean;
  onClose: () => void;
  subroutineId: number;
  onVariablePicked: (newVariable: VariableRef) => void;
  type: string[];
  scope?: VariableScope[];
};

const VariablePicker: FC<VariablePickerProps> = (props) => {
  const { metadata } = useMetadata();
  const { application } = useApplication();
  const localRef = useRef<null | HTMLDivElement>(null);

  const [searchBlocksItem, setSearchBlocksItem] = useState<SearchBlocksItem | undefined>();
  const [localVariables, setLocalVariables] = useState<Variable[]>();
  const [selectedVariable, setSelectedVariable] = useState<VariableRef>();
  const [collapseVariables, setCollapseVariables] = useState(defaultCollapse);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useLocalStorage<VariableSortType>('variableSort', 'date');
  const [invert, setInvert] = useState<boolean>(false);
  const [openManager, setOpenManager] = useState(false);
  const [managerScope, setManagerScope] = useState<VariableScope>('global');
  const [showVariables, setShowVariables] = useState(false);

  const currentSubroutine = findSubroutine(application.subroutines, props.subroutineId);

  const handleVariableCollapse = (scope: VariableScope, collapse: boolean) => {
    setCollapseVariables((prevVars) => ({ ...prevVars, [scope]: collapse }));
  };

  const sortedScopes = props.scope?.includes('local')
    ? ([...props.scope.filter((a) => a !== 'local'), 'local'] as VariableScope[])
    : props.scope;

  /**
   * Only show records from metadata.typelists, if props.type is from there.
   * @param record
   * @returns A Record<string,string> with only accepted types from the typelist, otherwise returns the unaltered Record.
   */
  const filterListType = (record: Variable[], typesToShow: string[]): Variable[] | undefined => {
    const foundListType = metadata.typelists?.find((lType) => typesToShow.some((ts) => ts === lType.name));
    if (!foundListType) return;
    const filteredRecord = record.filter((variable) => foundListType.types.some((flType) => flType === variable.type));
    return filteredRecord;
  };

  const renderEntries = (record: Variable[] | undefined, scope: VariableScope, isMetadata?: boolean) => {
    if (!record || collapseVariables[scope]) return <></>;

    // First only list properties that correlate to metatada.datatypes.
    const valueTypedRecord = record.filter((variable) =>
      metadata.datatypes.some((vType) => vType.type === variable.type)
    );

    // Then check if type correlates to any of the metadata.typelists and show only those variables
    const typelistFilteredRecord = filterListType(valueTypedRecord, props.type);
    const filteredRecord =
      typelistFilteredRecord || valueTypedRecord.filter((variable) => props.type.some((val) => val === variable.type));

    const filteredVars = filterByQuery(filteredRecord, searchQuery);
    return renderListItemButton(sortVariables(filteredVars, sortBy, invert), scope, isMetadata);
  };

  const renderListItemButton = (record: Variable[], scope: VariableScope, isMetadata?: boolean) =>
    record.map((variable, index) => {
      const variableIsUsed = variableIsInUse(variable, scope, application, currentSubroutine);
      if (isMetadata && !showVariables && !variableIsUsed && !searchQuery) return;

      return (
        <ListItemButton
          key={`var.${scope}.${variable.name}.${index}`}
          selected={selectedVariable?.name === variable.name && selectedVariable.scope === scope}
          onClick={() =>
            setSelectedVariable({
              name: variable.name,
              scope: scope
            })
          }
          onDoubleClick={() => {
            props.onVariablePicked({
              name: variable.name,
              scope: scope
            });
            handleClose();
          }}
          sx={{ ml: 2 }}
        >
          <Tooltip title={variableIsUsed ? 'Variable is in use' : 'Variable is unused'}>
            <CircleIcon sx={{ color: variableIsUsed ?? 'transparent', pr: '5px' }} />
          </Tooltip>
          <ListItemText primary={variable.name} /> <code>{variable.type}</code>
        </ListItemButton>
      );
    });

  const handleClose = () => {
    setSelectedVariable(undefined);
    props.onClose();
  };

  const getSubroutineName = (): ReactNode | false => {
    if (!currentSubroutine) return false;

    return (
      <>
        {' '}
        <code>{currentSubroutine.label}</code>
      </>
    );
  };

  useEffect(() => {
    setTimeout(() => {
      localRef.current?.scrollIntoView();
    }, 10);
  }, [localRef.current, props.open]);

  const CustomListSubHeader: FC<{
    children: ReactNode;
    scope: VariableScope;
  }> = ({ children, scope }) => {
    return (
      <ListSubheader
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Box>
          {collapseVariables[scope] ? (
            <IconButton tooltip='Expand variables' onClick={() => handleVariableCollapse(scope, false)}>
              <ChevronRight />
            </IconButton>
          ) : (
            <IconButton tooltip='Collapse variables' onClick={() => handleVariableCollapse(scope, true)}>
              <ExpandMore />
            </IconButton>
          )}
          {children}
        </Box>
        <Box>
          <IconButton
            tooltip='Edit variables'
            onClick={() => {
              setManagerScope(scope);
              setOpenManager(true);
            }}
          >
            <Tune />
          </IconButton>
        </Box>
      </ListSubheader>
    );
  };

  const getScopedRender = (scope: VariableScope, index: number) => {
    if (scope === 'local') {
      return (
        <Box key={`variable-scope-${index}`}>
          <CustomListSubHeader scope='local'>Local{getSubroutineName()}</CustomListSubHeader>
          {renderEntries(localVariables, 'local')}
        </Box>
      );
    }

    return (
      <Box key={`variable-scope-${index}`}>
        <CustomListSubHeader scope={scope}>{scope}</CustomListSubHeader>
        {renderEntries(metadata.variables?.[scope], scope)}
        {renderEntries(application.variables?.[scope], scope)}
      </Box>
    );
  };

  useEffect(() => {
    setSearchQuery('');
    if (!props.open || !application.variables.local || !props.subroutineId) return;
    if (application.variables.local[props.subroutineId]) {
      setLocalVariables(application.variables.local[props.subroutineId]);
    }
  }, [props.open, props.subroutineId, application.variables.local]);

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        <Stack direction='row' justifyContent='space-between'>
          <Stack direction='row' gap={1}>
            Pick Variable
            {sortedScopes && sortedScopes.map((scope, index) => <code key={`variable-title-${index}`}>{scope}</code>)}
          </Stack>
          <Button variant='outlined' onClick={() => setShowVariables(!showVariables)}>
            {`${showVariables ? 'Hide' : 'Show'} System Variables`}
          </Button>
        </Stack>
        {props.type && !props.type.includes('substart') && (
          <Typography variant='body2' color='text.secondary'>
            <code>{props.type.toString()}</code>
          </Typography>
        )}
        <SearchField
          searchQuery={searchQuery}
          setSearchQuery={(query) => setSearchQuery(query)}
          sortBy={sortBy}
          invert={invert}
          onSort={(changeSortBy, reverse) => {
            changeSortBy && setSortBy(changeSortBy as VariableSortType);
            reverse && setInvert(!invert);
          }}
          searchOptions={variableSearchOptions}
          customTooltip={variableCustomTooltip}
          autoFocus={props.open}
        />
      </DialogTitle>
      {application && props.open && (
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <List
            subheader={<li />}
            sx={{
              '& ul': { padding: 0 }
            }}
          >
            {sortedScopes ? (
              sortedScopes.map((scope, index) => getScopedRender(scope, index))
            ) : (
              <>
                <Box ref={localRef}>
                  <CustomListSubHeader scope='local'>Local{getSubroutineName()}</CustomListSubHeader>
                  {renderEntries(localVariables, 'local')}
                </Box>
                <CustomListSubHeader scope='session'>Session</CustomListSubHeader>
                {renderEntries(metadata.variables?.session, 'session', true)}
                {renderEntries(application.variables?.session, 'session')}
                <CustomListSubHeader scope='channel'>Channel</CustomListSubHeader>
                {renderEntries(metadata.variables?.channel, 'channel', true)}
                {renderEntries(application.variables?.channel, 'channel')}
                <CustomListSubHeader scope='task'>Task</CustomListSubHeader>
                {renderEntries(metadata.variables?.task, 'task', true)}
                {renderEntries(application.variables?.task, 'task')}
                <CustomListSubHeader scope='global'>Global</CustomListSubHeader>
                {renderEntries(metadata.variables?.global, 'global', true)}
                {renderEntries(application.variables?.global, 'global')}
                <CustomListSubHeader scope='config'>Config</CustomListSubHeader>
                {renderEntries(metadata.variables?.config, 'config', true)}
                {renderEntries(application.variables?.config, 'config')}
              </>
            )}
          </List>
          <FindReferencesDialog searchItem={searchBlocksItem} onClose={() => setSearchBlocksItem(undefined)} />
        </DialogContent>
      )}
      <DialogActions>
        <Button onClick={handleClose} color='primary'>
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (!selectedVariable) return;
            props.onVariablePicked(selectedVariable);
            handleClose();
          }}
          color='primary'
          disabled={!selectedVariable}
        >
          Select
        </Button>
      </DialogActions>
      <VariableDialog
        open={openManager}
        onClose={() => setOpenManager(false)}
        scope={managerScope}
        onVariableSearch={(searchItem) => setSearchBlocksItem(searchItem)}
      />
    </Dialog>
  );
};

export default VariablePicker;
